import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { LearningResource } from 'src/app/models/learning_resource.model';
import { LearningResourceAchievement } from 'src/app/models/learning_resource_achievement.model';
import { User } from 'src/app/models/user.model';
import { ApiService } from 'src/app/services';

export interface LearningResourceAchievementFormDialogData {
  mode: 'create' | 'edit';
  user: User;
  learning_resource_achievement?: LearningResourceAchievement;
}

@Component({
  selector: 'app-learning-resource-achievement-form-dialog',
  templateUrl: './learning-resource-achievement-form-dialog.component.html',
  styleUrl: './learning-resource-achievement-form-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
  ],
})
export class LearningResourceAchievementFormDialogComponent implements OnInit {
  private readonly dialog_ref = inject(MatDialogRef<LearningResourceAchievementFormDialogComponent, boolean>);
  private readonly api_service = inject(ApiService);
  private readonly snack_bar = inject(MatSnackBar);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly form_builder = inject(FormBuilder);

  readonly data = inject<LearningResourceAchievementFormDialogData>(MAT_DIALOG_DATA);

  readonly loading = signal(false);
  readonly learning_resources = signal<LearningResource[]>([]);
  readonly is_edit_mode = this.data.mode === 'edit';

  readonly form = this.form_builder.group({
    learning_resource_id: [this.data.learning_resource_achievement?.learning_resource_id ?? (null as number | null), Validators.required],
    start_date: [this.parseDate(this.data.learning_resource_achievement?.start_date), Validators.required],
    end_date: [this.parseDate(this.data.learning_resource_achievement?.end_date), Validators.required],
  });

  ngOnInit(): void {
    this.initLearningResources();
  }

  save(): void {
    if (this.loading() || this.form.invalid) {
      return;
    }

    this.loading.set(true);

    const body = {
      user_id: this.data.user.id,
      learning_resource_id: this.form.value.learning_resource_id!,
      start_date: this.formatDate(this.form.value.start_date),
      end_date: this.formatDate(this.form.value.end_date),
    };

    const request$ =
      this.is_edit_mode && this.data.learning_resource_achievement
        ? this.api_service.updateLearningResourceAchievement(this.data.learning_resource_achievement.id, body)
        : this.api_service.createLearningResourceAchievement(body);

    request$.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe({
      next: (): void => this.dialog_ref.close(true),
      error: (err: { error?: { error?: string } }): void => {
        this.loading.set(false);
        const message = err?.error?.error ?? 'Error al guardar el logro.';
        this.snack_bar.open(message, undefined, { duration: 5000 });
      },
    });
  }

  close(): void {
    if (!this.loading()) {
      this.dialog_ref.close(false);
    }
  }

  private initLearningResources(): void {
    this.api_service
      .getLearningResources({ order_by: 'name:asc' })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (response: ArrayResponse<LearningResource> | LearningResource[]): void => {
          const learning_resources = Array.isArray(response) ? response : response.data;
          this.learning_resources.set(learning_resources);
        },
      });
  }

  private parseDate(value: string | null | undefined): Date | null {
    if (!value) {
      return null;
    }

    const [year, month, day] = value.split('-').map(part => parseInt(part, 10));

    return new Date(year, month - 1, day);
  }

  private formatDate(value: Date | null | undefined): string {
    if (!value) {
      return '';
    }

    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');

    return `${year}-${month}-${day}`;
  }
}
